import React from 'react'
import DashboardIcon from '@mui/icons-material/Dashboard';
import People from '@mui/icons-material/People';
import CrisisAlertIcon from '@mui/icons-material/CrisisAlert';
import InsertInvitationIcon from '@mui/icons-material/InsertInvitation';
import DevicesOtherIcon from '@mui/icons-material/DevicesOther';
import EmailIcon from '@mui/icons-material/Email';
import ManageAccountsIcon from '@mui/icons-material/ManageAccounts';
import PersonRemoveIcon from '@mui/icons-material/PersonRemove';
import LanguageIcon from '@mui/icons-material/Language';
import TuneIcon from '@mui/icons-material/Tune';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import { DataObject, Diversity3, PlaylistAddCheck } from '@mui/icons-material';
import FactCheckIcon from '@mui/icons-material/FactCheck';
import CorporateFareIcon from '@mui/icons-material/CorporateFare';
import AssessmentIcon from '@mui/icons-material/Assessment';
import MapIcon from '@mui/icons-material/Map';
import SecurityIcon from '@mui/icons-material/Security';
import ScienceIcon from '@mui/icons-material/Science';
import CampaignIcon from '@mui/icons-material/Campaign';
import DescriptionIcon from '@mui/icons-material/Description';
import SupportAgentIcon from '@mui/icons-material/SupportAgent';

import drawOnMapConstants from '../../components/event-map/draw-on-map/drawOnMapConstants';
import { SystemUserTypes } from '../../types';
import { permissionCheck, hasIntakeAccess, isIntakeOnlyUser } from '../../helpers/permissionCheck';
import { customerPermissionFlags } from '../../constants/systemPermissions.constants';

export interface SidebarItem {
    title: string;
    path: string;
    icon: React.ReactNode;
}

export interface SidebarSection {
    subheader: string;
    items: SidebarItem[];
}

const sidebarConfig: SidebarSection[] = [
    {
        subheader: 'Safety Hub',
        items: [
            {
                title: 'Risk Assessment',
                path: '/risk-assessment',
                icon: <FactCheckIcon />,
            },
            {
                title: 'Dashboard',
                path: '/my-dashboard',
                icon: <DashboardIcon />,
            },
            {
                title: 'Event Map',
                path: '/event-map',
                icon: <MapIcon />
            },
            {
                title: 'POM Users',
                path: '/users',
                icon: <People />,
            },
            {
                title: 'Devices',
                path: '/devices',
                icon: <DevicesOtherIcon />,
            },
            {
                title: 'Message Center',
                path: '/messages',
                icon: <EmailIcon />,
            },
            {
                title: 'Alerts',
                path: '/alerts',
                icon: <CrisisAlertIcon />,
            },
            {
                title: 'Test Mode',
                path: '/test-mode',
                icon: <ScienceIcon />,
            },
            {
                title: 'Reports',
                path: '/reports',
                icon: <AssessmentIcon />,
            },
            {
                title: 'Appointments',
                path: '/appointments',
                icon: <InsertInvitationIcon />,
            },
        ],
    },
    {
        subheader: 'Admin',
        items: [
            {
                title: 'System Users',
                path: '/system-users',
                icon: <ManageAccountsIcon />,
            },
            {
                title: 'Groups & Teams',
                path: '/groups-teams',
                icon: <Diversity3 />,
            },
            {
                title: 'Settings',
                path: '/settings',
                icon: <TuneIcon />,
            },
            {
                title: 'Notifications',
                path: '/settings/notifications',
                icon: <NotificationsActiveIcon />,
            },
            {
                title: 'Deletion Requests',
                path: '/account-deletion-requests',
                icon: <PersonRemoveIcon />,
            },
        ],
    },
];

const pomMasterSidebarConfig: SidebarSection[] = [
    {
        subheader: 'Admin',
        items: [
            {
                title: 'Customers',
                path: '/customers',
                icon: <CorporateFareIcon />,
            },
            {
                title: 'System Users',
                path: '/system-users',
                icon: <ManageAccountsIcon />,
            },
            {
                title: 'AUTH CODES',
                path: '/auth-codes',
                icon: <SecurityIcon />,
            },
            {
                title: 'Banners',
                path: '/banners',
                icon: <CampaignIcon />,
            },
            {
                title: 'Settings (v2)',
                path: '/pom-master/settings',
                icon: <TuneIcon />,
            },
            {
                title: 'Audit Log Explorer',
                path: '/audit-log-explorer',
                icon: <DescriptionIcon />,
            },
            {
                title: 'Support Agent',
                path: '/support-agent',
                icon: <SupportAgentIcon />,
            },
        ],
    },
]

export interface SidebarFlags {
    showMessageCenter?: boolean;
    showTestMode?: boolean;
    showDeletionRequests?: boolean;
    showRiskAssessment?: boolean;
    showNotificationRules?: boolean;
    organizationType?: string;
}

// The only nav item an IRA-standalone org gets. The product stands on its
// own: platform features it does not have are not shown at all. System Users
// was dropped too (2026-08-28 call: no adding people during the trial);
// profile/password management lives in the account menu. Keyed on path, not
// display title, so renaming a label can't change what standalone orgs see.
const STANDALONE_ACTIVE_PATHS = ['/risk-assessment'];

export const renderSidebarItems = (authUser: SystemUserTypes.Model, flags: SidebarFlags = {}) => {
    const {
        showMessageCenter = true,
        showTestMode = false,
        showDeletionRequests = false,
        showRiskAssessment = false,
        showNotificationRules = false,
        organizationType = 'full',
    } = flags;

    if (authUser.pomMaster && !authUser.masquarade) {
        return pomMasterSidebarConfig;
    }

    let sidebar: SidebarSection[] = [...sidebarConfig];

    // Per-user Intake Risk Assessment gate. Previously the Risk Assessment tab
    // showed to EVERY user in an IRA-enabled org (no role gate — the CVS-class
    // entitlement leak). Now it is gated on the intake capability: admins/super
    // admins implicitly, plus anyone carrying INTAKE_RISK_ASSESSMENT.
    const intakeOnly = isIntakeOnlyUser(authUser);
    // Show the tab only if the user has intake access AND the org actually
    // offers IRA (org flag, a standalone org, or the user is intake-only — which
    // only happens inside an IRA-enabled org).
    const canSeeRiskAssessment = hasIntakeAccess(authUser)
        && (showRiskAssessment || organizationType === 'iraStandalone' || intakeOnly);

    // Copy the sections instead of assigning into sidebarConfig: settings
    // resolve after first render, and a mutation here would hide a gated item
    // for the rest of the session even once its setting turns on.
    const hiddenTitles = [
        ...(!showMessageCenter ? ['Message Center'] : []),
        ...(!showTestMode ? ['Test Mode'] : []),
        ...(!showDeletionRequests ? ['Deletion Requests'] : []),
        ...(!showNotificationRules ? ['Notifications'] : []),
        // Standalone orgs and intake-only users keep Risk Assessment regardless
        // of the feature flag, mirroring the route guard: it is their only nav
        // item, so hiding it here would empty every section and leave a blank
        // sidebar (and the API item below would have no section to land in).
        ...(!canSeeRiskAssessment ? ['Risk Assessment'] : []),
    ];
    if (hiddenTitles.length > 0) {
        sidebar = sidebar.map((section) => ({
            ...section,
            items: section.items.filter((item) => !hiddenTitles.includes(item.title)),
        }));
    }

    const isSuperAdmin = permissionCheck(customerPermissionFlags.SUPER_ADMIN);
    if (isSuperAdmin) {
        sidebar.push({
            subheader: 'Super Admin',
            items: [
                {
                    title: 'Bulk Actions',
                    path: '/bulk-actions',
                    icon: <PlaylistAddCheck />,
                },
            ],
        });
    }

    if (authUser.masquarade && !sidebar.some(s => s.subheader === 'POM Master Only (Masquerade)')) {
        sidebar.push(
            {
                subheader: 'POM Master Only (Masquerade)',
                items: [
                    {
                        title: drawOnMapConstants.drawOnMapTitle,
                        path: drawOnMapConstants.drawOnMapPath,
                        icon: <LanguageIcon />,
                    },
                ],
            },
        )
    }

    // Strip the nav down to the IRA-only experience for BOTH an IRA-standalone
    // org and a per-user intake specialist (their sole access is intake). Tabs
    // outside the plan are removed entirely, not greyed out (team decision
    // 2026-08-27 — the product must stand on its own). Applied after all
    // appends, so Bulk Actions and the masquerade Geofence tool go too.
    if (organizationType === 'iraStandalone' || intakeOnly) {
        sidebar = sidebar
            .map((section) => ({
                ...section,
                items: section.items.filter((item) => STANDALONE_ACTIVE_PATHS.includes(item.path)),
            }))
            .filter((section) => section.items.length > 0);

        // The API docs tab is a STANDALONE-ORG affordance only. An intake
        // specialist inside a full org runs assessments in-app and never
        // touches the API, so they do not get it (design: intake experience =
        // standalone view MINUS the API callout).
        if (organizationType === 'iraStandalone') {
            sidebar[0]?.items.push({
                title: 'API',
                path: '/api-access',
                icon: <DataObject />,
            });
        }
    }

    // Adding empty section to create space at the bottom of the navbar
    sidebar.push({
        subheader: '',
        items: [],
    });

    return sidebar;
}

export type SideBarConfig = typeof sidebarConfig;

export default sidebarConfig;
